import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormButton, Input, string } from '@ijoin/components';
import {
  usePostAdminAuthMfaEnroll,
  usePostAdminAuthMfaGenerate,
  usePostLogin,
  usePostVerifyMfa
} from '@ijoin/hooks';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  Typography
} from '@mui/material';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const credentialsResolver = zodResolver(
  z.object({
    password: string({ required: true }),
    username: string({ required: true })
  })
);

const mfaResolver = zodResolver(
  z.object({
    mfaCode: string({ required: true })
  })
);

const LoginPage = () => {
  const [mfaLoginId, setMfaLoginId] = useState('');
  const [step, setStep] = useState<'credentials' | 'mfa' | 'mfaSetup'>(
    'credentials'
  );

  const postLogin = usePostLogin();
  const postVerifyMfa = usePostVerifyMfa();
  const generateMfa = usePostAdminAuthMfaGenerate();
  const enrollMfa = usePostAdminAuthMfaEnroll({
    mutation: {
      onSuccess: body => {
        localStorage.setItem('isAdmin', body.isAdmin ? '1' : '0');
        window.location.href = '/dashboard';
      }
    }
  });

  const credentialsMethods = useForm({
    defaultValues: { password: '', username: '' },
    mode: 'onTouched',
    resolver: credentialsResolver
  });

  const mfaMethods = useForm({
    defaultValues: { mfaCode: '' },
    mode: 'onTouched',
    resolver: mfaResolver
  });

  const onSubmitCredentials = (data: {
    password: string;
    username: string;
  }) => {
    postLogin.mutate(
      { data },
      {
        onSuccess: body => {
          if (body.requiresMfaSetup) {
            setStep('mfaSetup');
            generateMfa.mutate();
            return;
          }

          if (body.requiresMfa) {
            setMfaLoginId(body.loginId ?? '');
            setStep('mfa');
            return;
          }

          localStorage.setItem('isAdmin', body.isAdmin ? '1' : '0');
          window.location.href = '/dashboard';
        }
      }
    );
  };

  const onSubmitMfa = (data: { mfaCode: string }) => {
    postVerifyMfa.mutate(
      { data: { code: data.mfaCode, loginId: mfaLoginId } },
      {
        onSuccess: body => {
          localStorage.setItem('isAdmin', body.isAdmin ? '1' : '0');
          window.location.href = '/dashboard';
        }
      }
    );
  };

  const onSubmitMfaSetup = (data: { mfaCode: string }) => {
    enrollMfa.mutate({ data: { code: data.mfaCode } });
  };

  const onBack = () => {
    setStep('credentials');
    mfaMethods.reset();
    postVerifyMfa.reset();
    generateMfa.reset();
    enrollMfa.reset();
  };

  const isMfaSetup = step === 'mfaSetup';
  const mfaMutation = isMfaSetup ? enrollMfa : postVerifyMfa;

  const credentialsError = (postLogin.error as { message?: string })?.message;
  const mfaError = (mfaMutation.error as { message?: string })?.message;

  const redirectingFromCredentials =
    postLogin.isSuccess && !postLogin.data?.requiresMfa;

  if (
    (step === 'credentials' &&
      (postLogin.isPending || redirectingFromCredentials)) ||
    (step !== 'credentials' && (mfaMutation.isPending || mfaMutation.isSuccess))
  ) {
    return null;
  }

  if (step !== 'credentials') {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ height: '100vh' }}>
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <Form
            methods={mfaMethods}
            noValidate
            onSubmit={mfaMethods.handleSubmit(
              isMfaSetup ? onSubmitMfaSetup : onSubmitMfa
            )}>
            <CardHeader
              title={
                isMfaSetup
                  ? 'Set Up Two-Factor Authentication'
                  : 'Two-Factor Authentication'
              }
            />
            <CardContent>
              <Stack spacing={2}>
                {isMfaSetup && (
                  <Stack alignItems='center' spacing={2}>
                    <Typography variant='body2'>
                      Two-factor authentication is required for your account.
                      Scan the QR code with an authenticator app, then enter the
                      6-digit code.
                    </Typography>
                    {generateMfa.data && (
                      <img
                        alt='QR Code'
                        data-testid='mfaSetupQrCode'
                        src={generateMfa.data.qrCodeDataUrl}
                        style={{ height: 200, width: 200 }}
                      />
                    )}
                    {generateMfa.isError && (
                      <Alert severity='error'>Failed to generate QR code</Alert>
                    )}
                  </Stack>
                )}
                <Input
                  data-testid='mfaCode'
                  format='string'
                  hideError
                  label='Verification Code'
                  name='mfaCode'
                />
                {mfaMutation.isError && (
                  <Alert severity='error'>
                    {mfaError || 'Invalid verification code'}
                  </Alert>
                )}
              </Stack>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', pb: 2, px: 2 }}>
              <Button data-testid='back' onClick={onBack}>
                Back
              </Button>
              <FormButton
                data-testid='verifyMfa'
                disabled={mfaMutation.isPending}
                triggerValidation
                variant='contained'>
                Verify
              </FormButton>
            </CardActions>
          </Form>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack alignItems='center' justifyContent='center' sx={{ height: '100vh' }}>
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <Form
          methods={credentialsMethods}
          noValidate
          onSubmit={credentialsMethods.handleSubmit(onSubmitCredentials)}>
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <Box
              alt='iJoin'
              component='img'
              src='/resources/images/login-logo.webp'
              sx={{ maxHeight: '5rem' }}
            />
          </Box>
          <CardContent>
            <Stack spacing={2}>
              <Input
                data-testid='username'
                format='string'
                hideError
                label='Username'
                name='username'
              />
              <Input
                data-testid='password'
                format='password'
                hideError
                label='Password'
                name='password'
              />
              {postLogin.isError && (
                <Alert severity='error'>
                  {credentialsError || 'Invalid credentials'}
                </Alert>
              )}
            </Stack>
          </CardContent>
          <CardActions sx={{ pb: 2, px: 2 }}>
            <FormButton
              data-testid='signIn'
              disabled={postLogin.isPending}
              fullWidth
              size='large'
              triggerValidation
              variant='contained'>
              Login
            </FormButton>
          </CardActions>
        </Form>
      </Card>
    </Stack>
  );
};

export const Route = createFileRoute('/login')({
  beforeLoad: async () => {
    const res = await window.fetch('/api/v3/auth/admin', {
      credentials: 'include'
    });

    if (res.ok) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: LoginPage
});
